// /var/www/kikai/src/pages/BusquedaNP.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { ipService } from '../services/ipService';
import { Search, Package, Wrench, CheckCircle, XCircle, Info, ChevronDown, Eye } from 'lucide-react';
import axios from 'axios';

interface SearchResult {
  part_number: string;
  part_name: string;
  part_description: string;
  motor_code: string;
  configuration_code: string;
  equipment_family: string | null;
  system_group: string;
  assembly_name: string;
  effectivity: string | null;
  direct_url: string | null;
  sisweb_url: string;
}

interface SearchStats {
  total: number;
  motors: number;
  configs: number;
}

interface PartSuggestion {
  part_number: string;
  part_name: string;
}
const API_URL = '/parts';

const BusquedaNP: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  
  const [motorCode, setMotorCode] = useState('');
  const [configCode, setConfigCode] = useState('');
  const [partNumber, setPartNumber] = useState('');
  
  const [motorSuggestions, setMotorSuggestions] = useState<string[]>([]);
  const [configSuggestions, setConfigSuggestions] = useState<string[]>([]);
  const [partSuggestions, setPartSuggestions] = useState<PartSuggestion[]>([]);
  
  const [showMotorDropdown, setShowMotorDropdown] = useState(false);
  const [showConfigDropdown, setShowConfigDropdown] = useState(false);
  const [showPartDropdown, setShowPartDropdown] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [stats, setStats] = useState<SearchStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 50;

  const motorRef = useRef<HTMLDivElement>(null);
  const configRef = useRef<HTMLDivElement>(null);
  const partRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    checkAccess();
    loadStats();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (motorRef.current && !motorRef.current.contains(event.target as Node)) {
        setShowMotorDropdown(false);
      }
      if (configRef.current && !configRef.current.contains(event.target as Node)) {
        setShowConfigDropdown(false);
      }
      if (partRef.current && !partRef.current.contains(event.target as Node)) {
        setShowPartDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getToken = () => localStorage.getItem('token');

  const checkAccess = async () => {
    try {
      const response = await axios.get(`${API_URL}/check-access`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (!response.data.hasAccess) {
        setError('No tienes acceso a esta funcionalidad.');
      }
    } catch (err: any) {
      setError('Error verificando acceso');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error cargando estadisticas:', err);
    }
  };

  const fetchMotorSuggestions = async (query: string) => {
    if (query.length < 1) { setMotorSuggestions([]); return; }
    try {
      const response = await axios.get(`${API_URL}/suggestions/motors`, {
        params: { query },
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMotorSuggestions(response.data.suggestions);
      setShowMotorDropdown(true);
    } catch (err) { console.error('Error sugerencias motores:', err); }
  };

  const handleMotorChange = (value: string) => {
    setMotorCode(value.toUpperCase());
    fetchMotorSuggestions(value);
    setConfigCode('');
    setPartNumber('');
  };

  const selectMotor = (motor: string) => {
    setMotorCode(motor);
    setShowMotorDropdown(false);
  };

  const fetchConfigSuggestions = async (query: string) => {
    if (query.length < 1) { setConfigSuggestions([]); return; }
    try {
      const params: any = { query };
      if (motorCode) params.motorCode = motorCode;
      const response = await axios.get(`${API_URL}/suggestions/configs`, {
        params,
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setConfigSuggestions(response.data.suggestions);
      setShowConfigDropdown(true);
    } catch (err) { console.error('Error sugerencias configs:', err); }
  };

  const handleConfigChange = (value: string) => {
    setConfigCode(value.toUpperCase());
    fetchConfigSuggestions(value);
    setPartNumber('');
  };

  const selectConfig = (config: string) => {
    setConfigCode(config);
    setShowConfigDropdown(false);
  };

  const fetchPartSuggestions = async (query: string) => {
    if (query.length < 1) { setPartSuggestions([]); return; }
    try {
      const params: any = { query };
      if (motorCode) params.motorCode = motorCode;
      if (configCode) params.configCode = configCode;
      const response = await axios.get(`${API_URL}/suggestions/parts`, {
        params,
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPartSuggestions(response.data.suggestions);
      setShowPartDropdown(true);
    } catch (err) { console.error('Error sugerencias partes:', err); }
  };

  const handlePartChange = (value: string) => {
    setPartNumber(value.toUpperCase());
    fetchPartSuggestions(value);
  };

  const selectPart = (part: PartSuggestion) => {
    setPartNumber(part.part_number);
    setShowPartDropdown(false);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!motorCode && !configCode && !partNumber) {
      setError('Ingresa al menos un criterio de busqueda');
      return;
    }

    setLoading(true);
    setError(null);
    setResults([]);
    setCurrentPage(1);

    try {
      const publicIp = await ipService.getPublicIP();
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        publicIp: publicIp
      };

      const response = await axios.post(`${API_URL}/search`, {
        motorCode: motorCode || null,
        configCode: configCode || null,
        partNumber: partNumber || null,
        deviceInfo 
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      setResults(response.data.results);
      if (response.data.results.length === 0) {
        setError('No se encontraron resultados');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error en la busqueda');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMotorCode('');
    setConfigCode('');
    setPartNumber('');
    setResults([]);
    setError(null);
    setCurrentPage(1);
    setMotorSuggestions([]);
    setConfigSuggestions([]);
    setPartSuggestions([]);
  };

  const openSISWeb = (url: string | null) => {
    if (url) window.open(url, '_blank');
  };

  // Navegar al chat con auto-fetch de detalles en sidebar
  const openPartDetails = (result: SearchResult) => {
    navigate('/dashboard/chat', {
      state: {
        autoFetchPart: {
          partNumber: result.part_number,
          url: result.direct_url,
          motorCode: result.motor_code,
          configCode: result.configuration_code,
          partName: result.part_name
        }
      }
    });
  };

  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const currentResults = results.slice(indexOfFirstResult, indexOfLastResult);
  const totalPages = Math.ceil(results.length / resultsPerPage);

  if (error && error.includes('acceso')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <Info className="w-6 h-6 text-yellow-600" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200">
                  Funcionalidad Premium
                </h3>
                <p className="text-yellow-700 dark:text-yellow-300 mt-2">
                  La busqueda de numeros de parte requiere permisos especiales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Busqueda de Numeros de Parte
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Base de datos con {stats?.total.toLocaleString() || '0'} partes de {stats?.motors || '0'} motores Caterpillar
          </p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Partes</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <Wrench className="w-5 h-5 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Motores</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.motors}</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Configuraciones</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.configs}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              
              {/* MOTOR */}
              <div ref={motorRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Modelo / Motor *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={motorCode}
                    onChange={(e) => handleMotorChange(e.target.value)}
                    onFocus={() => motorCode && setShowMotorDropdown(true)}
                    placeholder="Ej: C175, 3126, 993K"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                {showMotorDropdown && motorSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {motorSuggestions.map((motor, index) => (
                      <button key={index} type="button" onClick={() => selectMotor(motor)}
                        className="w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white">
                        {motor}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* CONFIGURACION */}
              <div ref={configRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Configuracion *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={configCode}
                    onChange={(e) => handleConfigChange(e.target.value)}
                    onFocus={() => configCode && setShowConfigDropdown(true)}
                    placeholder="Ej: 14T, PHB, Z4D"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                {showConfigDropdown && configSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {configSuggestions.map((config, index) => (
                      <button key={index} type="button" onClick={() => selectConfig(config)}
                        className="w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white">
                        {config}
                        {motorCode && <span className="text-xs text-gray-500 ml-2">({motorCode})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* NUMERO DE PARTE */}
              <div ref={partRef} className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pieza/Parte (NP) *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={partNumber}
                    onChange={(e) => handlePartChange(e.target.value)}
                    onFocus={() => partNumber && setShowPartDropdown(true)}
                    placeholder="Ej: 419-4895"
                    className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                {showPartDropdown && partSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {partSuggestions.map((part, index) => (
                      <button key={index} type="button" onClick={() => selectPart(part)}
                        className="w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-700">
                        <div className="font-mono text-sm text-gray-900 dark:text-white">{part.part_number}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{part.part_name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button type="submit" disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <Search className="w-5 h-5" />
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
              <button type="button" onClick={handleClear}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                Limpiar
              </button>
            </div>
          </form>
        </div>

        {error && !error.includes('acceso') && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Resultados ({results.length})
              </h2>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Anterior</span>
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Pagina {currentPage} de {totalPages}
                  </span>
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Siguiente</span>
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">N Parte</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Nombre</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Motor</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Config</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Grupo</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Familia</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300 text-center w-24">Detalles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {currentResults.map((result, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                        {result.part_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {result.part_name + (result.effectivity ? ' (' + result.effectivity + ')' : '')}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {result.motor_code}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {result.configuration_code}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {result.system_group}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {result.equipment_family}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openPartDetails(result)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
                          title={`Ver detalles de ${result.part_number}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusquedaNP;